/** @jsxImportSource preact */
import type {
  EnvelopeMessage,
  EnvelopeAction,
  ProductGridData,
  ProductDetailData,
  CartSummaryData,
  CheckoutEmbedData,
  OrderStatusData,
  ActionRequestData,
  ListPickerData,
} from '../types';
import type { ActionHandler } from './types';
import { TextBubble } from './TextBubble';
import { ProductGrid } from './ProductGrid';
import { ProductDetail } from './ProductDetail';
import { CartSummary } from './CartSummary';
import { OrderStatusBadge } from './OrderStatusBadge';
import { HandoffNotice } from './HandoffNotice';
import { CheckoutLink } from './CheckoutLink';
import { ActionButton } from './ActionButton';
import { QuickReplyButtons } from './QuickReplyButtons';
import { ListMessage } from './ListMessage';

interface MessageListProps {
  messages: EnvelopeMessage[];
  actions?: EnvelopeAction[];
  onAction: ActionHandler;
  onOpenCheckout?: () => void;
  /** Render "Finalizar compra" on cart_summary cards (false when the host has a cart bar). */
  cartCheckout?: boolean;
}

/**
 * Top-level renderer for the typed envelope path. Dispatches each
 * message to the appropriate component by `type`. Unknown types fall
 * back to a TextBubble using `content` (forward compatibility).
 *
 * IMPORTANT: every text path uses JSX text interpolation, never
 * dangerouslySetInnerHTML. Preact escapes by default — that is the
 * XSS guarantee for this layer.
 */
export function MessageList({
  messages,
  actions,
  onAction,
  onOpenCheckout,
  cartCheckout = true,
}: MessageListProps) {
  const safeMessages = Array.isArray(messages) ? messages : [];
  const safeActions = Array.isArray(actions) ? actions : [];

  return (
    <div class="lc-envelope-list">
      {safeMessages.map((message, index) => {
        const role = message.role === 'system' ? 'system' : 'assistant';
        const key = `env-msg-${index}`;

        switch (message.type) {
          case 'text': {
            return (
              <TextBubble key={key} role={role}>
                {message.content || ''}
              </TextBubble>
            );
          }
          case 'product_grid': {
            const grid = (message.data || {}) as ProductGridData;
            const products = Array.isArray(grid.products) ? grid.products : [];
            return (
              <div class={`lc-message ${role}`} key={key}>
                <ProductGrid
                  products={products}
                  onAction={onAction}
                  eyebrow={message.content || 'Seleccionado para vos'}
                />
              </div>
            );
          }
          case 'product_detail': {
            const detail = (message.data || {}) as ProductDetailData;
            if (!detail.product) {
              return (
                <TextBubble key={key} role={role}>
                  {message.content || ''}
                </TextBubble>
              );
            }
            return (
              <div class={`lc-message ${role}`} key={key}>
                <ProductDetail product={detail.product} onAction={onAction} />
              </div>
            );
          }
          case 'cart_summary': {
            const cart = (message.data || {}) as CartSummaryData;
            return (
              <div class={`lc-message ${role}`} key={key}>
                {message.content ? (
                  <div class="lc-message-bubble">{message.content}</div>
                ) : null}
                <CartSummary cart={cart} onAction={onAction} showCheckout={cartCheckout} />
              </div>
            );
          }
          case 'checkout_embed': {
            const data = (message.data || {}) as CheckoutEmbedData;
            return (
              <div class={`lc-message ${role}`} key={key}>
                {message.content ? (
                  <div class="lc-message-bubble">{message.content}</div>
                ) : null}
                <CheckoutLink
                  onOpen={() => {
                    if (onOpenCheckout) onOpenCheckout();
                  }}
                  label={data.mode === 'wallet-add-card' ? 'Agregar tarjeta' : 'Abrir checkout'}
                />
              </div>
            );
          }
          case 'order_status': {
            const data = (message.data || {}) as OrderStatusData;
            return (
              <div class={`lc-message ${role}`} key={key}>
                {message.content ? (
                  <div class="lc-message-bubble">{message.content}</div>
                ) : null}
                <OrderStatusBadge data={data} />
              </div>
            );
          }
          case 'action_request': {
            const data = (message.data || {}) as ActionRequestData;
            const buttons = Array.isArray(data.buttons) ? data.buttons : [];
            const text = data.text || message.content;
            // Nothing actionable ⇒ fall back to a plain text bubble.
            if (buttons.length === 0) {
              return (
                <TextBubble key={key} role={role}>
                  {text || ''}
                </TextBubble>
              );
            }
            return (
              <QuickReplyButtons
                key={key}
                text={text}
                buttons={buttons}
                onAction={onAction}
              />
            );
          }
          case 'list_picker': {
            const data = (message.data || {}) as ListPickerData;
            const sections = Array.isArray(data.sections) ? data.sections : [];
            const text = data.text || message.content;
            if (sections.length === 0) {
              return (
                <TextBubble key={key} role={role}>
                  {text || ''}
                </TextBubble>
              );
            }
            return (
              <ListMessage
                key={key}
                text={text}
                button_label={data.button_label}
                sections={sections}
                onAction={onAction}
              />
            );
          }
          case 'handoff_notice': {
            return <HandoffNotice key={key} message={message.content} />;
          }
          default: {
            // Forward-compat: unknown types render as text using
            // `content` if present.
            return (
              <TextBubble key={key} role={role}>
                {message.content || ''}
              </TextBubble>
            );
          }
        }
      })}
      {safeActions.length > 0 ? (
        <div class="lc-envelope-actions">
          {safeActions.map((action) => (
            <ActionButton
              key={action.id}
              action={action}
              onAction={onAction}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
